import { supabase } from "../supabaseClient";
import { ScheduleEntry } from "../types/Profile";

/**
 * Validates that a schedule entry has the correct format
 */
function isValidScheduleEntry(entry: any): entry is ScheduleEntry {
  return (
    entry &&
    typeof entry === "object" &&
    typeof entry.day === "string" &&
    typeof entry.time === "string" &&
    entry.day.trim() !== "" &&
    entry.time.trim() !== "" &&
    /^\d{2}:\d{2}$/.test(entry.time) // Validates HH:MM format
  );
}

/**
 * Gets the user's timezone preference, with fallback to browser/system timezone
 */
function getUserTimezone(profileTimezone?: string): string {
  if (profileTimezone && profileTimezone !== "UTC") {
    return profileTimezone;
  }

  // Fallback to browser timezone
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/**
 * Gets the next available posting slot based on the user's posting schedule
 * and existing posts.
 */
export async function getNextAvailablePostingSlot(): Promise<Date> {
  try {
    console.log("🕐 Calculating next available posting slot...");

    // Get the current user's profile with posting schedule and timezone (gracefully handle if timezone column doesn't exist)
    let profile: any = null;
    let profileError: any = null;

    const result = await supabase
      .from("profiles")
      .select("posting_schedule, timezone")
      .single();
    profile = result.data;
    profileError = result.error;

    if (profileError || !profile?.posting_schedule) {
      console.log("📅 No posting schedule found, using default date");
      // Fallback to default behavior if no schedule is configured
      return getDefaultDate();
    }

    const schedule = profile.posting_schedule as ScheduleEntry[];

    if (!Array.isArray(schedule) || schedule.length === 0) {
      console.log("📅 Empty posting schedule, using default date");
      return getDefaultDate();
    }

    // Validate schedule entries
    const validSchedule = schedule.filter(isValidScheduleEntry);
    if (validSchedule.length === 0) {
      console.log("📅 No valid schedule entries found, using default date");
      return getDefaultDate();
    }

    console.log("📅 Found posting schedule:", validSchedule);

    // Get user's timezone (from profile or fallback to browser timezone)
    const userTimezone = getUserTimezone(profile?.timezone);
    console.log("🌍 Using timezone:", userTimezone);

    // Get existing posts for the next 120 days to check for conflicts
    const startDate = new Date();
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + 120);

    const { data: existingPosts, error: postsError } = await supabase
      .from("posts")
      .select("publish_at")
      .gte("publish_at", startDate.toISOString())
      .lte("publish_at", endDate.toISOString())
      .order("publish_at", { ascending: true });

    if (postsError) {
      console.warn("Error fetching existing posts:", postsError);
      return getDefaultDate();
    }

    console.log(
      "📄 Found",
      existingPosts?.length || 0,
      "existing posts in next 120 days"
    );

    // Create a set of occupied time slots for quick lookup
    const occupiedSlots = new Set(
      (existingPosts || []).map((post) =>
        new Date(post.publish_at!).toISOString()
      )
    );

    // Create a set of occupied dates (YYYY-MM-DD) to prevent multiple posts per day
    const occupiedDates = new Set(
      (existingPosts || []).map((post) => {
        const date = new Date(post.publish_at!);
        return date.toISOString().split("T")[0]; // Get YYYY-MM-DD format
      })
    );

    console.log("🚫 Occupied slots:", Array.from(occupiedSlots));
    console.log("📅 Occupied dates:", Array.from(occupiedDates));

    // Find the latest scheduled post to determine starting point
    let startingDate = new Date();
    if (existingPosts && existingPosts.length > 0) {
      const latestPost = existingPosts.reduce((latest, current) => {
        const currentDate = new Date(current.publish_at!);
        const latestDate = new Date(latest.publish_at!);
        return currentDate > latestDate ? current : latest;
      });

      startingDate = new Date(latestPost.publish_at!);
      console.log("📅 Latest scheduled post:", startingDate.toISOString());
    }

    // Sort schedule entries by day and time for consistent ordering
    const sortedSchedule = [...validSchedule].sort((a, b) => {
      const dayOrder = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ];
      const dayA = dayOrder.indexOf(a.day.toLowerCase());
      const dayB = dayOrder.indexOf(b.day.toLowerCase());

      if (dayA !== dayB) {
        return dayA - dayB;
      }

      return a.time.localeCompare(b.time);
    });

    // Find the next available slot
    const now = new Date();
    let currentDate = new Date(Math.max(now.getTime(), startingDate.getTime()));

    // Start checking from the day after the latest post (or tomorrow if no posts exist)
    currentDate.setDate(currentDate.getDate() + 1);
    currentDate.setHours(0, 0, 1, 0);

    console.log(
      "🔍 Searching for available slots starting from:",
      currentDate.toISOString(),
      currentDate.toLocaleString("en-US", { timeZone: userTimezone })
    );

    // Look for available slots in the next 120 days
    for (let day = 0; day < 120; day++) {
      const checkDate = new Date(currentDate);
      checkDate.setDate(checkDate.getDate() + day);

      const dayName = checkDate
        .toLocaleDateString("en-CA", { weekday: "long" })
        .toLowerCase();

      // Find all schedule entries for this day
      const daySchedules = sortedSchedule.filter(
        (entry) => entry.day.toLowerCase() === dayName
      );

      console.log(`🔍 Checking schedule for ${dayName}`, daySchedules);

      // Check if this date already has a scheduled post
      const dateKey = checkDate.toISOString().split("T")[0]; // Get YYYY-MM-DD format
      if (occupiedDates.has(dateKey)) {
        console.log(
          `📅 Date ${dateKey} already has a scheduled post, skipping`
        );
        continue; // Skip this entire day if it already has a post
      }

      for (const scheduleEntry of daySchedules) {
        // Use timezone-aware date creation
        const potentialDate = checkDate;
        const [hours, minutes] = scheduleEntry.time.split(":").map(Number);
        potentialDate.setHours(hours, minutes, 0, 0);

        // Skip if this time is in the past
        if (potentialDate <= now) {
          continue;
        }

        console.log(
          `🔍 Checking potential slot: ${potentialDate.toISOString()} -- LOCAL: ${potentialDate.toLocaleString(
            "en-US",
            { timeZone: userTimezone }
          )} (${scheduleEntry.time})`
        );

        // Check if this slot is already occupied
        const slotKey = potentialDate.toISOString();
        console.log("🔍 Slot key:", slotKey);
        if (!occupiedSlots.has(slotKey)) {
          console.log("✅ Found available slot:", potentialDate.toISOString());

          // Get timezone offset for the user's timezone at this date
          const tempDate = new Date(
            potentialDate.toLocaleString("en-US", { timeZone: userTimezone })
          );
          const utcDate = new Date(
            potentialDate.toLocaleString("en-US", { timeZone: "UTC" })
          );
          const offset = tempDate.getTime() - utcDate.getTime();

          console.log(
            ` Timezone offset for ${userTimezone}: ${
              offset / 1000 / 60 / 60
            } hours`
          );

          potentialDate.setTime(potentialDate.getTime() + offset); // Adjust to UTC

          // The date will be truncated to until 16 characters. The ISO date must have the correct date and time with timezone offset.
          return potentialDate;
        } else {
          console.log("❌ Slot occupied:", slotKey);
        }
      }
    }

    // If no available slot found in schedule, return default
    console.log("⚠️ No available slots found in schedule, using default");
    return getDefaultDate();
  } catch (error) {
    console.error("Error calculating next posting slot:", error);
    return getDefaultDate();
  }
}

/**
 * Default date calculation - fallback when no schedule is configured
 */
export function getDefaultDate(): Date {
  const tomorrow = new Date();
  tomorrow.setUTCHours(tomorrow.getUTCHours() + 24);
  const defaultDate = new Date(tomorrow);
  defaultDate.setUTCHours(8);
  defaultDate.setUTCMinutes(30);
  return defaultDate;
}
